import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { ArrowLeft, Send } from 'lucide-react';
import { intervisieApi, DUUR_OPTIES, METHODIEKEN } from '../services/intervisieApi';
import { toast } from 'react-hot-toast';

interface AttendeeRow {
  user_id: string;
  name: string;
  role: 'facilitator' | 'inbrenger' | 'deelnemer';
  attended: boolean;
}

export const IntervisieSessionRegistreerPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: group, isLoading } = useQuery({
    queryKey: ['intervisie-group', id],
    queryFn: () => intervisieApi.getGroup(id!),
    enabled: !!id,
  });

  const [date, setDate] = useState('');
  const [methodology, setMethodology] = useState('');
  const [duur, setDuur] = useState(120);
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState<AttendeeRow[]>([]);

  React.useEffect(() => {
    if (!group) return;
    // Alleen vullen zolang de lijst nog leeg is (actuele waarde via prev).
    setAttendees((prev) =>
      prev.length > 0
        ? prev
        : group.members.map((m) => ({
            user_id: m.user_id,
            name: `${m.first_name} ${m.last_name}`,
            role: 'deelnemer',
            attended: true,
          }))
    );
  }, [group]);

  const registerMutation = useMutation({
    mutationFn: () =>
      intervisieApi.registerSession({
        group_id: id!,
        date,
        methodology,
        duur_minuten: duur,
        notes: notes || undefined,
        attendees: attendees
          .filter((a) => a.attended)
          .map((a) => ({ user_id: a.user_id, role: a.role, attended: true })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intervisie-group', id] });
      toast.success(t('intervisie.sessionRegistered'));
      navigate(`/intervisie/${id}`);
    },
    onError: (err: any) => {
      const msgs = err?.response?.data?.errors;
      if (msgs?.length) {
        toast.error(msgs[0]);
      } else {
        toast.error(err?.response?.data?.message || t('intervisie.error'));
      }
    },
  });

  const updateAttendee = (userId: string, field: keyof AttendeeRow, value: any) => {
    setAttendees((prev) =>
      prev.map((a) => (a.user_id === userId ? { ...a, [field]: value } : a))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) { toast.error(t('intervisie.dateRequired')); return; }
    if (!methodology) { toast.error(t('intervisie.methodologyRequired')); return; }
    const presentAttendees = attendees.filter((a) => a.attended);
    if (presentAttendees.length < 2) {
      toast.error(t('intervisie.minAttendees'));
      return;
    }
    const facilitators = presentAttendees.filter((a) => a.role === 'facilitator');
    if (facilitators.length !== 1) {
      toast.error(t('intervisie.exactlyOneFacilitator'));
      return;
    }
    registerMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse h-6 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card><p className="text-gray-500 text-center py-8">{t('intervisie.groupNotFound')}</p></Card>
      </div>
    );
  }

  if (!group.is_contact_person) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-500">{t('intervisie.onlyContact')}</p>
            <Link to={`/intervisie/${id}`} className="text-[#0B1F3A] hover:underline mt-2 inline-block">
              {t('intervisie.backToGroup')}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/intervisie" className="hover:text-[#0B1F3A]">{t('intervisie.title')}</Link>
        <span>›</span>
        <Link to={`/intervisie/${id}`} className="hover:text-[#0B1F3A] flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {group.name}
        </Link>
        <span>›</span>
        <span className="text-gray-700">{t('intervisie.sessionRegisterTitle')}</span>
      </div>

      <Card>
        <h1 className="text-xl font-bold text-gray-900 mb-6">{t('intervisie.sessionRegisterTitle')}</h1>
        <p className="text-sm text-gray-600 mb-6">{t('intervisie.sessionRegisterDesc')}</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.sessionDate')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.sessionMethodology')} <span className="text-red-500">*</span>
            </label>
            <select
              value={methodology}
              onChange={(e) => setMethodology(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            >
              <option value="">{t('intervisie.selectMethodology')}</option>
              {METHODIEKEN.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.sessionDuration')} <span className="text-red-500">*</span>
            </label>
            <select
              value={duur}
              onChange={(e) => setDuur(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            >
              {DUUR_OPTIES.map((m) => (
                <option key={m} value={m}>{t('intervisie.durationOption', { hours: (m / 60).toLocaleString('nl-NL') })}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{t('intervisie.sessionDurationHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('intervisie.attendees')} <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-400 mb-3">{t('intervisie.attendeesHint')}</p>
            <div className="space-y-2">
              {attendees.map((a) => (
                <div
                  key={a.user_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    a.attended ? 'border-[#0B1F3A] bg-blue-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={a.attended}
                    onChange={(e) => updateAttendee(a.user_id, 'attended', e.target.checked)}
                    className="rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]"
                  />
                  <span className="flex-1 text-sm font-medium text-gray-800">{a.name}</span>
                  <select
                    value={a.role}
                    onChange={(e) => updateAttendee(a.user_id, 'role', e.target.value)}
                    disabled={!a.attended}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0B1F3A] disabled:opacity-50"
                  >
                    <option value="deelnemer">{t('intervisie.roleParticipant')}</option>
                    <option value="facilitator">{t('intervisie.roleFacilitator')}</option>
                    <option value="inbrenger">{t('intervisie.roleContributor')}</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('intervisie.notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('intervisie.notesPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate(`/intervisie/${id}`)}>
              {t('intervisie.cancel')}
            </Button>
            <Button type="submit" loading={registerMutation.isPending}>
              <Send className="w-4 h-4 mr-2" />
              {t('intervisie.sessionRegister')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
